import React from 'react';
import { Download } from 'react-feather';
import { Modal } from 'react-bootstrap';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { translate } from '../../translations';
import DialogFooter from '../DialogFooter/DialogFooter';
import Button from '../Button/Button';
import FileUploader from '../FileUploader/FileUploader';
import './Dialog.css';
import {
    H4,
    Paragraph1,
    Paragraph2,
    sanitizePropertyValue,
    TransitionOpacity,
    Subhead1,
} from '../../theme/styles';
import Form from '../Form/Form';

// TODO: We need to make this more generic, see JIRA: https://movista.atlassian.net/browse/UPN-9179

export class Dialog extends React.PureComponent {
    renderDialogInput = (input) => (
        <>
            {input?.errorMsg ? (
                <CSSTransition
                    in={input?.errorMsg}
                    key={input?.errorMsg}
                    classNames={TransitionOpacity}
                    timeout={160}
                    mountOnEnter
                    unmountOnExit
                >
                    <div className="importErrorMsgContainer overlay-scrollbar-thin" style={Paragraph1}>
                        <span style={{ color: sanitizePropertyValue('--text_danger') }}>*</span>
                        {input?.errorMsg}
                    </div>
                </CSSTransition>
            ) : null}
            <input
                placeholder={input?.placeholder}
                value={input?.value}
                onChange={input.onChange}
                type="text"
                className="modalDialogInput"
                style={Paragraph2}
            />
        </>
    );

    renderDialogFileImport = (fileImport, fileTemplateDownload, customErrorHeader, customWarningHeader, loadingProgressData, secondaryContent) => (
        <>
            {fileImport?.importWarningMsg?.length > 0 ? (
                <SwitchTransition>
                    <CSSTransition
                        in={fileImport?.importWarningMsg?.length > 0}
                        key={fileImport?.importWarningMsg?.length > 0}
                        classNames={TransitionOpacity}
                        timeout={50}
                        mountOnEnter
                        unmountOnExit
                    >
                        <div>
                            <div className="importWarningMsgContainer overlay-scrollbar-thin" style={Paragraph1}>
                                <span className="warningLabelHeader" style={Subhead1}>
                                    {customWarningHeader || translate('review_warnings_below_and_import_updates_if_needed')}
                                </span>
                                {fileImport?.importWarningMsg.map((msg) => (
                                    <div key={msg} className="importWarningMsg">
                                        <span style={{ color: sanitizePropertyValue('--text_danger') }}>*</span>
                                        <span>{msg}</span>
                                    </div>
                                ))}
                            </div>
                            {fileImport?.importWarningMsg?.length > 0 && fileImport?.importErrorMsg?.length > 0 ? (<div><hr /></div>) : null}
                        </div>
                    </CSSTransition>
                </SwitchTransition>
            ) : null}
            {fileImport?.importErrorMsg?.length > 0 ? (
                <SwitchTransition>
                    <CSSTransition
                        in={fileImport?.importErrorMsg?.length > 0}
                        key={fileImport?.importErrorMsg?.length > 0}
                        classNames={TransitionOpacity}
                        timeout={50}
                        mountOnEnter
                        unmountOnExit
                    >
                        <div className="importErrorMsgContainer overlay-scrollbar-thin" style={Paragraph1}>
                            <span className="errorLabelHeader" style={Subhead1}>
                                {customErrorHeader || translate('import_unsuccessful_please_review_errors')}
                            </span>
                            {fileImport?.importErrorMsg.map((msg) => (
                                <div key={msg} className="importErrorMsg">
                                    <span style={{ color: sanitizePropertyValue('--text_danger') }}>*</span>
                                    <span>{msg}</span>
                                </div>
                            ))}
                        </div>
                    </CSSTransition>
                </SwitchTransition>
            ) : null}
            <div className="fileImportContainer">
                <Button
                    variant="secondary"
                    className="templateButton"
                    icon={(fileTemplateDownload?.icon) ? fileTemplateDownload.icon : <Download className="templateIcon" size={18} />}
                    content={fileTemplateDownload?.text}
                    onClick={fileTemplateDownload?.onClick}
                    disabled={fileImport?.importInProgressStatus || fileTemplateDownload?.downloadInProgress}
                />
                <div className="fileImportUploadContainer">
                    <FileUploader
                        fileUploadText={fileImport?.fileUploadText}
                        fileName={fileImport?.fileName}
                        fileAcceptTypes={fileImport?.fileAcceptTypes}
                        importErrorMsg={fileImport?.importErrorMsg}
                        disabled={fileImport?.importInProgressStatus}
                        onFileSave={fileImport?.onFileSave}
                        onFileDelete={fileImport?.onFileDelete}
                        loadingProgressData={loadingProgressData}
                    />
                </div>
            </div>
            {secondaryContent && (
                <div className="fileImportSecondaryContent">
                    {secondaryContent}
                </div>
            )}
        </>
    );

    renderActionContent = ({ content }) => (
        <div className="actionDialogContainer">
            {content && (
                <div className="actionContentsContainer">
                    {content}
                </div>
            )}
        </div>
    );

    renderBody = (type) => {
        switch (type) {
            case 'input': {
                const { input } = this.props;
                return this.renderDialogInput(input);
            }
            case 'radio': {
                const { radio } = this.props;
                return (
                    <Form
                        groupName="dialogRadio"
                        inputType="radio"
                        data={radio?.options}
                        clickFunction={radio?.onOptionClick}
                    />
                );
            }
            case 'grid': {
                const {
                    grid,
                } = this.props;
                return grid.content;
            }
            case 'action': {
                const {
                    action,
                } = this.props;
                return this.renderActionContent(action);
            }
            case 'fileImport': {
                const {
                    fileImport,
                    fileTemplateDownload,
                    customErrorHeader,
                    customWarningHeader,
                    loadingProgressData,
                    secondaryContent,
                } = this.props;
                return this.renderDialogFileImport(
                    fileImport,
                    fileTemplateDownload,
                    customErrorHeader,
                    customWarningHeader,
                    loadingProgressData,
                    secondaryContent,
                );
            }
            case 'custom': {
                const { content } = this.props;
                return content;
            }
            default:
                return (<> </>);
        }
    };

    render() {
        const {
            type,
            size, // accept bootstrap-default: 'sm', 'lg', 'xl'. Also accept custom: 'xs', 'md' (medium), 'ml' (medium-large), 'xxl'
            icon,
            iconStyle,
            title,
            headerDivider = 'none', // accept ['shadow', 'line', 'none']
            titleDescription,
            footerButtons,
            centered,
            show,
            onHide,
            keyboard,
            backdrop,
            indentBody,
            bodyClassName,
            titleStyle, // If the Dialog type is 'grid', the titleStyle needs to be H2
            id,
            secondaryTitle,
            secondaryTitleStyle,
            dialogClassName, // allows class to be added to dialog that would override height/width (or other generic styles)
            testId,
        } = this.props;

        const headerClassNames = [
            'modal-header',
            'modalDialogHeader',
            headerDivider && `modalDialogHeader--${headerDivider}`,
        ].join(' ');

        const titleDescriptionClassNames = [
            'modalDialogHeaderTextContainer',
            indentBody && 'indentBody',
        ].join(' ');

        const bodyClassNames = [
            'modal-body',
            indentBody && 'indentBody',
            bodyClassName ?? null,
        ].join(' ').trim();

        return (
            <Modal
                id={id}
                show={show}
                centered={centered}
                size={size}
                onHide={onHide}
                keyboard={keyboard}
                backdrop={backdrop}
                scrollable
                dialogClassName={dialogClassName}
                data-testid={testId}
            >
                <div className={headerClassNames}>
                    <div className="modalDialogTitleContainer">
                        {icon ? (
                            <div className={`modalDialogIconContainer modalDialogIconContainer--${iconStyle || 'default'}`}>
                                {icon}
                            </div>
                        ) : null}
                        <div className="modal-title" style={titleStyle || H4}>
                            {title}
                        </div>
                        {secondaryTitle && (
                            <div className="modalSecondaryTitle" style={secondaryTitleStyle}>
                                {secondaryTitle}
                            </div>
                        )}
                    </div>
                    {titleDescription && (
                        <div className={titleDescriptionClassNames} style={Paragraph1}>
                            {titleDescription}
                        </div>
                    )}
                </div>
                {type && (
                    <div className={bodyClassNames}>
                        {this.renderBody(type)}
                    </div>
                )}
                <DialogFooter buttons={footerButtons} />
            </Modal>
        );
    }
}

export default Dialog;
